import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlassIcon, PlusIcon, ClipboardTextIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import SkeletonRows from "../../components/SkeletonRows";
import {
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
} from "../../components/crm/Form";
import StatusBadge from "../../components/ops/StatusBadge";
import api, { formatApiError } from "../../lib/api";

const STATUSES = [
  "new",
  "triage",
  "scheduled",
  "converted",
  "closed",
  "canceled",
];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const SOURCES = ["phone", "website", "referral", "internal"];
const SERVICE_TYPES = [
  "diagnostic",
  "repair",
  "maintenance",
  "inspection",
  "upgrade",
  "other",
];

const optsFrom = (arr) =>
  arr.map((v) => ({ value: v, label: v.replace(/_/g, " ") }));

export default function ServiceRequestsList() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const priority = params.get("priority") || "";
  const initialQ = params.get("q") || "";

  const [list, setList] = useState([]);
  const [q, setQ] = useState(initialQ);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const filters = {};
      if (status) filters.status = status;
      if (priority) filters.priority = priority;
      if (q) filters.q = q;
      const { data } = await api.get("/service-requests/", { params: filters });
      setList(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, priority]);

  // Phase 15 — auto-open create modal when navigated with ?new=1.
  useEffect(() => {
    if (params.get("new") === "1") {
      setOpen(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q) next.set("q", q);
    else next.delete("q");
    setParams(next, { replace: true });
    load();
  };

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <Layout title="Service Requests" subtitle="Intake">
      <EntityHeader
        overline="intake"
        title="Service Requests"
        subtitle="Inbound service tickets — triage and convert to work orders."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form
          onSubmit={submitSearch}
          className="relative flex-1 min-w-[240px] max-w-md"
        >
          <MagnifyingGlassIcon
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search issue summary…"
            data-testid="sr-search"
            className="w-full rounded-sm border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
          />
        </form>
        <div className="w-44">
          <Select
            value={status}
            onChange={(v) => setParam("status", v)}
            options={optsFrom(STATUSES)}
            placeholder="All statuses"
            data-testid="sr-status-filter"
          />
        </div>
        <div className="w-36">
          <Select
            value={priority}
            onChange={(v) => setParam("priority", v)}
            options={optsFrom(PRIORITIES)}
            placeholder="All priorities"
            data-testid="sr-priority-filter"
          />
        </div>
        <PrimaryButton
          onClick={() => setOpen(true)}
          data-testid="sr-create-button"
        >
          <PlusIcon size={14} weight="bold" /> New Request
        </PrimaryButton>
      </div>

      <ErrorBanner message={err} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="sr-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Issue</th>
              <th className="text-left px-5 py-3 font-bold">Customer</th>
              <th className="text-left px-5 py-3 font-bold">Priority</th>
              <th className="text-left px-5 py-3 font-bold">Status</th>
              <th className="text-left px-5 py-3 font-bold">Source</th>
              <th className="text-left px-5 py-3 font-bold">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && list.length === 0 && <SkeletonRows count={5} cols={6} />}
            {!loading && list.length === 0 && (
              <EmptyState
                inTable
                colSpan={6}
                icon={ClipboardTextIcon}
                title={
                  q || status || priority
                    ? "No matching service requests"
                    : "No service requests yet"
                }
                description={
                  q || status || priority
                    ? "Try clearing the filters or searching for a different term."
                    : "Service requests will appear here when customers contact you, or when monitoring auto-creates one from a critical alert."
                }
                cta={{
                  label: "New Service Request",
                  onClick: () => setOpen(true),
                  testId: "sr-empty-cta",
                }}
                testId="sr-empty"
              />
            )}
            {list.map((s) => (
              <tr
                key={s.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/service-requests/${s.id}`)}
                data-testid={`sr-row-${s.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900 max-w-md truncate">
                  {s.issue_summary}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {s.customer_name || "—"}
                </td>
                <td className="px-5 py-3">
                  <StatusBadge value={s.priority} />
                </td>
                <td className="px-5 py-3">
                  <StatusBadge value={s.status} />
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {s.intake_source}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-600 tabular">
                  {new Date(s.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateServiceRequestModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(s) => {
          setOpen(false);
          nav(`/service-requests/${s.id}`);
        }}
      />
    </Layout>
  );
}

export function CreateServiceRequestModal({ open, onClose, onCreated, prefill }) {
  const empty = useMemo(
    () => ({
      customer_id: "",
      property_id: "",
      solar_system_id: "",
      intake_source: "phone",
      issue_summary: "",
      issue_details: "",
      priority: "normal",
      requested_service_type: "diagnostic",
      preferred_date: "",
    }),
    []
  );
  const [form, setForm] = useState(empty);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [systems, setSystems] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(prefill ? { ...empty, ...prefill } : empty);
    setErr("");
    api.get("/customers/").then((r) => setCustomers(r.data)).catch(() => {});
  }, [open, empty, prefill]);

  useEffect(() => {
    if (!form.customer_id) {
      setProperties([]);
      return;
    }
    api
      .get("/properties/", { params: { customer_id: form.customer_id } })
      .then((r) => setProperties(r.data))
      .catch(() => {});
  }, [form.customer_id]);

  useEffect(() => {
    if (!form.property_id) {
      setSystems([]);
      return;
    }
    api
      .get("/solar-systems/", { params: { property_id: form.property_id } })
      .then((r) => setSystems(r.data))
      .catch(() => {});
  }, [form.property_id]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        property_id: form.property_id,
        intake_source: form.intake_source,
        issue_summary: form.issue_summary,
        priority: form.priority,
        requested_service_type: form.requested_service_type,
      };
      if (form.solar_system_id) payload.solar_system_id = form.solar_system_id;
      if (form.issue_details) payload.issue_details = form.issue_details;
      if (form.preferred_date) payload.preferred_date = form.preferred_date;
      const { data } = await api.post("/service-requests/", payload);
      onCreated(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Service Request"
      subtitle="capture intake"
      data-testid="sr-create-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Customer" required>
          <Select
            value={form.customer_id}
            onChange={(v) =>
              setForm({ ...form, customer_id: v || "", property_id: "" })
            }
            options={customers.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="Select customer…"
            data-testid="sr-customer-select"
          />
        </Field>
        <Field label="Property" required>
          <Select
            value={form.property_id}
            onChange={(v) => setForm({ ...form, property_id: v || "" })}
            options={properties.map((p) => ({
              value: p.id,
              label: p.address,
            }))}
            placeholder={
              form.customer_id ? "Select property…" : "Pick customer first"
            }
            data-testid="sr-property-select"
            disabled={!form.customer_id}
          />
        </Field>
        <Field label="Solar System (optional)">
          <Select
            value={form.solar_system_id}
            onChange={(v) => setForm({ ...form, solar_system_id: v || "" })}
            options={systems.map((s) => ({
              value: s.id,
              label: `${s.install_year} · ${s.original_installer || "system"}`,
            }))}
            placeholder={form.property_id ? "None" : "Pick property first"}
            data-testid="sr-system-select"
            disabled={!form.property_id}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Intake Source" required>
            <Select
              value={form.intake_source}
              onChange={(v) =>
                setForm({ ...form, intake_source: v || "phone" })
              }
              options={optsFrom(SOURCES)}
              placeholder=""
              data-testid="sr-source-select"
            />
          </Field>
          <Field label="Priority" required>
            <Select
              value={form.priority}
              onChange={(v) => setForm({ ...form, priority: v || "normal" })}
              options={optsFrom(PRIORITIES)}
              placeholder=""
              data-testid="sr-priority-select"
            />
          </Field>
        </div>
        <Field label="Service Type" required>
          <Select
            value={form.requested_service_type}
            onChange={(v) =>
              setForm({ ...form, requested_service_type: v || "diagnostic" })
            }
            options={optsFrom(SERVICE_TYPES)}
            placeholder=""
            data-testid="sr-type-select"
          />
        </Field>
        <Field label="Issue Summary" required>
          <TextInput
            required
            maxLength={300}
            value={form.issue_summary}
            onChange={(v) => setForm({ ...form, issue_summary: v })}
            data-testid="sr-summary-input"
          />
        </Field>
        <Field label="Details">
          <TextArea
            value={form.issue_details}
            onChange={(v) => setForm({ ...form, issue_details: v })}
            data-testid="sr-details-input"
            rows={4}
          />
        </Field>
        <Field label="Preferred Date">
          <input
            type="date"
            value={form.preferred_date}
            onChange={(e) =>
              setForm({ ...form, preferred_date: e.target.value })
            }
            data-testid="sr-preferred-date"
            className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="sr-submit"
          >
            {busy ? "Creating…" : "Create Request"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
